import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export type AccountStatus = 'active' | 'pending_review' | 'suspended' | 'locked' | 'inactive';

interface AccountStatusData {
  account_status: AccountStatus | null;
  locked_at: string | null;
}

/**
 * Lightweight query for account status - used for pending_review redirect
 */
export function useAccountStatusQuery(userId: string | null) {
  return useQuery<AccountStatusData>({
    queryKey: ['account_status', userId],
    queryFn: async () => {
      if (!userId) {
        return { account_status: null, locked_at: null };
      }

      const { data, error } = await supabase
        .from('profiles')
        .select('account_status, locked_at')
        .eq('id', userId)
        .maybeSingle();

      if (error) {
        throw error;
      }

      return {
        account_status: (data?.account_status as AccountStatus) ?? null,
        locked_at: data?.locked_at ?? null,
      };
    },
    enabled: !!userId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}
